import { apiFetch } from './client';
import { useAuth } from '@/lib/auth-store';

export type PaymentStatus =
  | 'initiated' | 'captured' | 'declined' | 'refunded' | 'failed'
  | 'partially_refunded';   // 0070

export interface PaymentTransactionRow {
  id: string;
  reseller_id: string;
  customer_id: string;
  caller_id: string | null;
  gateway_id: string;
  ivr_node_id: string | null;
  order_id: string | null;
  invoice_id: string | null;
  card_id: string | null;
  gateway_ref: string | null;
  amount_cents: number;
  currency: string;
  status: PaymentStatus;
  error_code: string | null;
  error_message: string | null;
  // 0070 additions
  category: string | null;
  refunded_amount_cents: number;
  last_refunded_at: string | null;
  is_test: boolean;
  caller_recording_id: string | null;
  attempt_number: number;
  parent_transaction_id: string | null;
  created_at: string;
  updated_at: string;
  gateway_name: string | null;
  gateway_kind: string | null;
  caller_display_name: string | null;
}

export interface TransactionsListResponse {
  rows: PaymentTransactionRow[];
  total: number;
  summary: {
    captured_count: number;
    captured_cents: number;
    declined_count: number;
    transactions_total: number;
  };
  limit: number;
  offset: number;
}

export interface ListParams {
  caller_id?: string;
  customer_id?: string;
  gateway_id?: string;
  status?: PaymentStatus;
  from?: string;
  to?: string;
  limit?: number;
  offset?: number;
}

export interface RefundBody {
  /** Omit for full refund. Must be ≤ remaining (amount - refunded). */
  amount_cents?: number;
  reason?: string;
}

export const paymentTransactions = {
  list: (p: ListParams = {}) => {
    const q = new URLSearchParams();
    for (const [k, v] of Object.entries(p)) {
      if (v !== undefined && v !== '') q.set(k, String(v));
    }
    return apiFetch<TransactionsListResponse>(`/api/payment-transactions?${q}`);
  },
  get: (id: string) =>
    apiFetch<PaymentTransactionRow>(`/api/payment-transactions/${id}`),

  /**
   * Refund a captured transaction. Server validates remaining balance +
   * calls the gateway adapter. Returns the updated row (with new status,
   * refunded_amount_cents, last_refunded_at).
   */
  refund: (id: string, body: RefundBody = {}) =>
    apiFetch<PaymentTransactionRow>(
      `/api/payment-transactions/${id}/refund`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  /**
   * CSV download. Same filter set as list(). Triggers a browser download
   * via the Bearer-authorized blob fetch helper.
   */
  downloadCsv: async (p: ListParams = {}): Promise<void> => {
    const q = new URLSearchParams();
    for (const [k, v] of Object.entries(p)) {
      if (v !== undefined && v !== '') q.set(k, String(v));
    }
    const url = `/api/payment-transactions.csv?${q}`;
    const accessToken = useAuth.getState().accessToken;
    const res = await fetch(url, {
      headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
      credentials: 'include',
    });
    if (!res.ok) throw new Error(`download_failed_${res.status}`);
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    try {
      const a = document.createElement('a');
      a.href = blobUrl;
      const cd = res.headers.get('Content-Disposition') ?? '';
      const m = /filename="?([^"]+)"?/.exec(cd);
      a.download = m?.[1] ?? `payments-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } finally {
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    }
  },
};
